import { LegislativeActionTypes, TiebreakRules, } from '@govinity/shared-config';
import { z } from 'zod';
export { LegislativeActionTypes, TiebreakRules };
export var CalculationBase;
(function (CalculationBase) {
    CalculationBase["PRESENT_AND_VOTING"] = "present_and_voting";
    CalculationBase["PRESENT"] = "present";
    CalculationBase["ALL_MEMBERS"] = "all_members";
})(CalculationBase || (CalculationBase = {}));
/**
 * Field shape shared by the create/update payloads and by callers that need the
 * raw object without the vote-requirement refinement — notably the support MCP
 * tool, which derives its input schema from `.partial().shape`.
 */
export const legislativeActionBaseSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    code: z.string().min(1, 'Code is required').max(255),
    description: z.string().nullable().optional(),
    orderById: z
        .number()
        .min(1, 'Order by is required')
        .optional()
        .describe('Position in action lists. Defaults to the end of the list when omitted.'),
    isActive: z.boolean(),
    type: z.enum(LegislativeActionTypes),
    isVoteRequired: z.boolean().optional().describe('Defaults to false.'),
    isSecondRequired: z.boolean().optional().describe('Defaults to false.'),
    requiresMeetingBodyReassignment: z
        .boolean()
        .optional()
        .describe('Taking the action requires picking a meeting body to refer the item to. Defaults to false.'),
    passLegislativeStatusId: z
        .number()
        .int()
        .positive()
        .nullish()
        .describe('Legislative status the item moves to when the action passes.'),
    failLegislativeStatusId: z
        .number()
        .int()
        .positive()
        .nullish()
        .describe('Legislative status the item moves to when the action fails.'),
    recommendedActionTemplate: z.string().nullish(),
    motionMadeTemplate: z.string().nullish(),
    passTemplate: z.string().nullish(),
    failTemplate: z.string().nullish(),
    isRollCallRequired: z.boolean().optional().describe('Defaults to false.'),
    isAbstentionAllowed: z.boolean().optional().describe('Defaults to false.'),
    tiebreakRule: z
        .enum(TiebreakRules)
        .nullish()
        .describe('How a tied vote resolves. Required when isVoteRequired is true.'),
});
/**
 * Validates that an action requiring a vote also declares how a tie resolves.
 *
 * Callers that only send part of an action (the support MCP tool, curl) must
 * evaluate this against the post-update state rather than the payload alone,
 * since either field may be absent and retain its stored value.
 */
export const hasValidTiebreakRule = ({ isVoteRequired, tiebreakRule, }) => {
    if (!isVoteRequired) {
        return true;
    }
    return tiebreakRule !== null && tiebreakRule !== undefined;
};
export const TIEBREAK_RULE_REQUIRED_MESSAGE = 'When voting is required, tiebreak rule must be specified';
const legislativeActionValidatedSchema = legislativeActionBaseSchema.refine(hasValidTiebreakRule, {
    message: TIEBREAK_RULE_REQUIRED_MESSAGE,
    path: ['tiebreakRule'],
});
/**
 * Form schema for react-hook-form. The admin form edits a complete action in
 * both create and edit modes, so it validates the whole object either way —
 * kept separate from the create schema so a create-only rule added later does
 * not silently start firing on the edit form.
 */
export const legislativeActionFormSchema = legislativeActionValidatedSchema;
export const legislativeActionCreateSchema = legislativeActionValidatedSchema;
/**
 * Update payloads are partial. The tiebreak pairing is deliberately not refined here as it depends
 * on the row being updated rather than the payload alone. The update controller checks the
 * pairing once it has the record in hand.
 */
export const legislativeActionUpdateSchema = legislativeActionBaseSchema.partial();
/**
 * Validates that a configured default action belongs to the configured available actions.
 */
export const hasValidDefaultAction = ({ actionIds, defaultActionId, }) => {
    if (defaultActionId !== null && defaultActionId !== undefined && actionIds !== undefined) {
        return actionIds.includes(defaultActionId);
    }
    return true;
};
export const DEFAULT_ACTION_MUST_BE_AVAILABLE_MESSAGE = 'Default action must be one of the selected available actions';
/**
 * Shared Zod refinement options for default action selection fields.
 */
export const defaultActionMustBeAvailableRefinement = {
    message: DEFAULT_ACTION_MUST_BE_AVAILABLE_MESSAGE,
    path: ['defaultActionId'],
};
